import { useContext, useCallback, useMemo, useState, useEffect } from 'react'
import styled from '@emotion/styled'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import Card from '@mui/material/Card'
import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Typography from '@mui/material/Typography'
import { object } from 'prop-types'
import { useSelector } from 'react-redux'
import { CvsTextField, CvsAutoComplete } from '@components'
import { connectorContext, folderContext } from '@contexts'
import { awsRegions } from '@global/constants'
import { templateCopyContext } from '../context'
import { FileFormPartial } from './file-form-partial'
import { NotificationEmailConfig } from './notification-email-config'

const FullWidth = styled.div`
  width: 100%;
`
const Flex = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`

const FlexRadioInline = styled(RadioGroup)`
  margin-top: 30px;
  display: flex;
  flex-direction: row;
`
const AutoComplete = styled(CvsAutoComplete)`
  margin-top: -8px;
  margin-right: 12px;
`

const ACLAutoComplete = styled(AutoComplete)`
  max-width: 430px;
  min-width: 430px;
  flex-grow: unset;
`
const ACLFlex = styled(Flex)`
  display: flex;
  flex-direction: row;
  justify-content: left;
  & > * {
    flex-grow: unset;
  }
  gap: 20px;
`

const AutoCompleteMore = styled(CvsAutoComplete)`
  margin-top: -20px;
  margin-bottom: -4px;
`

const MarginText = styled(CvsTextField)`
  margin-top: 12px;
  margin-bottom: 8px;
`

const StyledCard = styled(Card)`
  display: inline-flex;
`

const Monospace = styled.span`
  font-family: monospace;
  font-weight: bold;
`
const StyledTypography = styled(Typography)`
  margin-bottom: 12px;
`

const Warning = styled.div`
  background-color: #edcf69;
  width: 16px;
`

const Padding = styled.div`
  padding: 16px;
`

const Clickable = styled.span`
  text-decoration: underline;
  :hover {
    color: #25515b;
    cursor: pointer;
  }
`

export const S3out = ({ integration, assignment }) => {
  const { onChange } = useContext(templateCopyContext)
  const { byId } = useContext(connectorContext)
  const { byConnectorId, fetchFolderByConnectorId } = useContext(folderContext)
  const [showError, setShowError] = useState(false)
  const showIntegrationNotifications = useSelector(
    state => state.Auth.features['integration-notifications'],
  )
  const connectors = useMemo(
    () =>
      Object.values(byId)
        .filter(
          connector =>
            connector?.connector_type_id ===
            (assignment?.connector_type_id
              ? assignment?.connector_type_id
              : byId[assignment.connector_id]?.connector_type_id),
        )
        .map(connector => ({ value: connector.id, label: connector.name })),
    [byId, assignment],
  )

  const onS3Change = useCallback(
    (value, name) => {
      onChange(old => ({
        ...old,
        [assignment.id]: {
          ...assignment,
          [name]: value,
        },
      }))
    },
    [onChange, assignment],
  )

  const onS3MetadataChange = useCallback(
    (value, name) => {
      onChange(old => ({
        ...old,
        [assignment.id]: {
          ...assignment,
          metadata: {
            ...assignment.metadata,
            [name]: value,
          },
        },
      }))
    },
    [onChange, assignment],
  )

  useEffect(() => {
    if (!assignment.metadata.awsRegion) onS3MetadataChange(awsRegions.CURRENT[0].value, 'awsRegion')
  }, [])

  useEffect(() => {
    fetchFolderByConnectorId(assignment.connector_id)
      .then(() => {
        setShowError(false)
      })
      .catch(() => {
        setShowError(true)
      })
  }, [fetchFolderByConnectorId, assignment])

  const folderOptions = useMemo(
    () =>
      byConnectorId[assignment.connector_id] &&
      byConnectorId[assignment.connector_id].map(bucket => ({
        value: bucket.aws_bucket_name,
        label: bucket.aws_bucket_name,
      })),
    [byConnectorId, assignment],
  )

  return (
    <FullWidth>
      <FileFormPartial integration={integration} assignment={assignment}>
        <Flex>
          <AutoComplete
            label={t`AWS account`}
            selected={assignment.connector_id}
            onSelected={e => onS3Change(e, 'connector_id')}
            options={connectors || []}
            required
          />
          <AutoComplete
            label={t`Region`}
            selected={assignment.metadata.awsRegion || awsRegions.CURRENT[0].value}
            onSelected={value => onS3MetadataChange(value, 'awsRegion')}
            options={awsRegions.CURRENT || []}
            required
          />
          <FormControl variant="standard" component="fieldset">
            <FlexRadioInline
              value={assignment.folder_entry_type || 'list'}
              onChange={e => onS3Change(e.target.value, 'folder_entry_type')}
            >
              <FormControlLabel value="manual" control={<Radio />} label={t`Manual bucket entry`} />
              <FormControlLabel value="list" control={<Radio />} label={t`List buckets`} />
            </FlexRadioInline>
          </FormControl>
        </Flex>
        {assignment.folder_entry_type === 'manual' ? (
          <MarginText
            label={t`S3 bucket`}
            value={assignment.folder_name}
            onChange={e => onS3Change(e.target.value, 'folder_name')}
          />
        ) : !showError ? (
          <AutoCompleteMore
            label={t`S3 bucket`}
            selected={assignment.folder_name}
            onSelected={val => onS3Change(val, 'folder_name')}
            options={folderOptions || []}
          />
        ) : (
          <StyledCard>
            <Warning />
            <Padding>
              <StyledTypography>
                <b>{t`Hmm. Looks like we're missing some permissions.`}</b>
              </StyledTypography>
              <StyledTypography>
                <Trans>
                  Unfortunately, we were unable to retrieve your AWS S3 Buckets. This can happen
                  when the provided AWS key and secret have expired or have not been granted the{' '}
                  <Monospace>s3:ListBucket</Monospace> permission.
                </Trans>
              </StyledTypography>
              <StyledTypography>
                <Trans>
                  For more information about how to assign the <Monospace>s3:ListBucket</Monospace>{' '}
                  permission, please refer to the AWS S3 documentation at
                  https://docs.aws.amazon.com/AmazonS3/latest/dev/using-with-s3-actions.html.
                </Trans>
              </StyledTypography>
              <Typography>
                <Trans>
                  If you don't want to grant the <Monospace>s3:ListBucket</Monospace> permission,
                  you can manually enter the Bucket Name by clicking{' '}
                  <Clickable onClick={() => onS3Change('manual', 'folder_entry_type')}>
                    here
                  </Clickable>
                  .
                </Trans>
              </Typography>
            </Padding>
          </StyledCard>
        )}
        <ACLFlex>
          <ACLAutoComplete
            label={t`Object ownership setting`}
            selected={assignment.metadata.use_acl || '0'}
            onSelected={e => onS3MetadataChange(e, 'use_acl')}
            options={[
              {
                label: 'ACLs disabled (Bucket owner enforced)',
                value: '0',
              },
              {
                label: 'ACLs enabled (Bucket owner preferred)',
                value: 'bucket-owner-full-control',
              },
            ]}
          />
          <FormControlLabel
            label={t`Use server side encryption`}
            control={
              <Checkbox
                checked={assignment.metadata.use_sse !== '0'}
                value={assignment.metadata.use_sse !== '0'}
                onChange={({ target: { checked } }) =>
                  onS3MetadataChange(checked ? '1' : '0', 'use_sse')
                }
              />
            }
          />
        </ACLFlex>
      </FileFormPartial>
      {showIntegrationNotifications && (
        <NotificationEmailConfig
          integrationAssignmentId={assignment.id}
          connectorId={assignment.connector_id}
        />
      )}
    </FullWidth>
  )
}

S3out.propTypes = {
  assignment: object.isRequired,
  integration: object.isRequired,
}
